import { sortObject } from '@takeshape/util';
import { combineReducers } from 'redux';
import addToTakeShapeReducer from './add-to-takeshape/reducer.js';
import { AGENT_EDITOR_FORM_NAME } from './agent/components/agent-editor/constants.js';
import { agentFormReducer } from './agent/slice.js';
import { aiFormReducers } from './ai/slice.js';
import { NAME as API_EXPLORER_NAME, reducer as apiExplorerReducer } from './api-explorer/index.js';
import { NAME as API_KEYS_NAME } from './api-keys/constants.js';
import apiKeysReducer from './api-keys/reducer.js';
import { NAME as ASSET_PICKER_NAME } from './asset-picker/constants.js';
import assetPickerReducer from './asset-picker/reducer.js';
import { NAME as ASSET_UPLOADER_NAME } from './asset-uploader/constants.js';
import assetUploaderReducer from './asset-uploader/reducer.js';
import { NAME as ASSETS_NAME } from './assets/constants.js';
import assetsReducer from './assets/reducer.js';
import { NAME as AUTH_NAME } from './auth/constants.js';
import authFormReducers from './auth/form-reducers.js';
import authReducer from './auth/reducer.js';
import { NAME as BILLING_NAME } from './billing/constants.js';
import billingReducer from './billing/reducer.js';
import { NAME as BRANCHES_NAME } from './branches/constants.js';
import { reducer as branchesReducer } from './branches/reducer.js';
import { NAME as COLLAB_NAME } from './collab/constants.js';
import collabReducer from './collab/reducer.js';
import { NAME as CONFIRM_NAME } from './confirm/constants.js';
import confirmReducer from './confirm/reducer.js';
import { FORM_NAME as CONTENT_FORM_NAME, NAME as CONTENT_NAME } from './content/constants.js';
import contentFormReducer from './content/form-reducer.js';
import contentReducer from './content/reducer.js';
import { CONTENT_LIST_NAME, contentListReducer } from './content-list/index.js';
import { NAME as CONTENT_PREVIEW_NAME, reducer as contentPreviewReducer } from './content-preview/index.js';
import { CONTENT_SEARCH_NAME, contentSearchReducer } from './content-search/index.js';
import { NAME as EXPORT_NAME } from './export/constants.js';
import exportReducer from './export/reducer.js';
import { NAME as FORM_DIALOG_NAME } from './form-dialog/constants.js';
import formDialogReducer from './form-dialog/reducer.js';
import { guardrailsFormReducers } from './guardrails/form-reducers.js';
import { formReducer as createPublishTargetReducer } from './integrations/components/create-publish-target/index.js';
import { CREATE_PUBLISH_TARGET_FORM_NAME, NAME as INTEGRATIONS_NAME } from './integrations/constants.js';
import integrationsReducer from './integrations/reducer.js';
import { NAME as LIVE_LINKS_NAME, reducer as liveLinksReducer } from './live-links/index.js';
import {
  NAME as LOCALES_NAME,
  formReducers as localesFormReducers,
  reducer as localesReducer
} from './locales/index.js';
import { NAME as MESSAGES_NAME } from './messages/constants.js';
import messagesReducer from './messages/reducer.js';
import { MULTI_SELECT_NAME, multiSelectReducer } from './multi-select/index.js';
import { INVITE_FORM_NAME, NAME as PROJECT_NAME } from './project/constants.js';
import inviteUserFormReducer from './project/invite-user-form-reducer.js';
import projectReducer from './project/reducer.js';
import router from './router.js';
import { NAME as SCHEDULE_NAME } from './schedule/constants.js';
import scheduleReducer from './schedule/reducer.js';
import { SCHEMA_NAME, reducer as schemaReducer } from './schema/index.js';
import {
  SCHEMA_HISTORY_NAME,
  historyReducer as schemaHistoryReducer
} from './schema/slices/get-schema-version-list.js';
import { NAME as SCHEMA_FORM_NAME } from './schema-form/constants.js';
import schemaFormReducerFactory from './schema-form/reducer-factory.js';
import { NAME as SERVICES_NAME, servicesFormReducers, reducer as servicesReducer } from './services/index.js';
import { FORM_NAME as SITES_FORM_NAME, NAME as SITES_NAME } from './sites/constants.js';
import sitesFormReducer from './sites/form-reducer.js';
import sitesReducer from './sites/reducer.js';
import { NAME as SSO_NAME } from './sso/constants.js';
import ssoReducer from './sso/reducer.js';
import { LIST_NAME as USER_ROLE_LIST_NAME, NAME as USER_ROLE_NAME } from './user-roles/constants.js';
import userRoleListReducer from './user-roles/list-reducer.js';
import userRoleReducer from './user-roles/reducer.js';
import { LIST_NAME as VERSION_LIST_NAME, NAME as VERSION_NAME } from './versions/constants.js';
import versionListReducer from './versions/list-reducer.js';
import versionReducer from './versions/reducer.js';
import { NAME as WEBHOOKS_NAME } from './webhooks/constants.js';
import webhooksReducer from './webhooks/reducer.js';
import { NAME as WORKBENCH_NAME, reducer as workbenchReducer } from './workbench/index.js';
import { WORKFLOW_FORM_NAME, WORKFLOW_UPDATE_FORM } from './workflows/constants.js';
import workflowFormReducer from './workflows/form-reducer.js';

export default combineReducers(
  sortObject({
    [AUTH_NAME]: authReducer,
    [SCHEMA_NAME]: schemaReducer,
    [SCHEMA_HISTORY_NAME]: schemaHistoryReducer,
    [MESSAGES_NAME]: messagesReducer,
    [CONFIRM_NAME]: confirmReducer,
    [FORM_DIALOG_NAME]: formDialogReducer,
    [PROJECT_NAME]: projectReducer,
    [ASSETS_NAME]: assetsReducer,
    [ASSET_UPLOADER_NAME]: assetUploaderReducer,
    [ASSET_PICKER_NAME]: assetPickerReducer,
    [CONTENT_NAME]: contentReducer,
    [CONTENT_SEARCH_NAME]: contentSearchReducer,
    [SITES_NAME]: sitesReducer,
    [MULTI_SELECT_NAME]: multiSelectReducer,
    [BILLING_NAME]: billingReducer,
    [COLLAB_NAME]: collabReducer,
    [API_KEYS_NAME]: apiKeysReducer,
    [CONTENT_LIST_NAME]: contentListReducer,
    [WEBHOOKS_NAME]: webhooksReducer,
    [INTEGRATIONS_NAME]: integrationsReducer,
    [SCHEMA_FORM_NAME]: schemaFormReducerFactory({
      [CONTENT_FORM_NAME]: contentFormReducer,
      [INVITE_FORM_NAME]: inviteUserFormReducer,
      [SITES_FORM_NAME]: sitesFormReducer,
      [CREATE_PUBLISH_TARGET_FORM_NAME]: createPublishTargetReducer,
      [WORKFLOW_FORM_NAME]: workflowFormReducer,
      [WORKFLOW_UPDATE_FORM]: workflowFormReducer,
      [AGENT_EDITOR_FORM_NAME]: agentFormReducer,
      ...authFormReducers,
      ...localesFormReducers,
      ...servicesFormReducers,
      ...guardrailsFormReducers,
      ...aiFormReducers
    }),
    [LOCALES_NAME]: localesReducer,
    [LIVE_LINKS_NAME]: liveLinksReducer,
    [VERSION_NAME]: versionReducer,
    [VERSION_LIST_NAME]: versionListReducer,
    [USER_ROLE_NAME]: userRoleReducer,
    [USER_ROLE_LIST_NAME]: userRoleListReducer,
    [SCHEDULE_NAME]: scheduleReducer,
    [SSO_NAME]: ssoReducer,
    [CONTENT_PREVIEW_NAME]: contentPreviewReducer,
    addToTakeShape: addToTakeShapeReducer,
    [EXPORT_NAME]: exportReducer,
    [SERVICES_NAME]: servicesReducer,
    [API_EXPLORER_NAME]: apiExplorerReducer,
    [WORKBENCH_NAME]: workbenchReducer,
    [BRANCHES_NAME]: branchesReducer,
    router: router.reducer
  })
);
